import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!session;

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}/espace-client`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo }
    });
    return { error };
  };

  const signInWithMicrosoft = async () => {
    const redirectTo = `${window.location.origin}/espace-client`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: { redirectTo }
    });
    return { error };
  };
  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/espace-client`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      navigate('/clients/login');
    }
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/clients/login`,
    });
    return { error };
  };

  return {
    user,
    session,
    loading,
    isAuthenticated,
    signInWithEmail,
    signInWithGoogle,
    signInWithMicrosoft,
    signUp,
    signOut,
    resetPassword,
  };
}